import { TFunction } from 'react-i18next';
import Order, { Payment } from 'types/models/Order';
import { ReducerAction, ReducerState } from 'types/models/ReducerAction';
import parseAmounts from './parseAmounts';
import { SupportedLanguages } from 'types/enums/LanguageType';

export const initialStateForCloverPoints = [{ entityId: '', entityType: '', cloverPoints: [] }];

export const cloverPointsReducer = (state: ReducerState[], action: ReducerAction): ReducerState[] => {
  switch (action.type) {
    case 'new-venue-clover-points': {
      if (action.operation === 'add') {
        const { entityId, entityType, cloverPoints } = action.payload;
        const stateWithNewVenueCloverPointsAdded = state.concat({ entityId, entityType, cloverPoints });
        const arrFilteredByNewVenueId = stateWithNewVenueCloverPointsAdded.filter(
          (config) => config.entityId === action.payload.entityId,
        );
        return arrFilteredByNewVenueId;
      }
    }
    case 'new-commerce-clover-points':
      if (action.operation === 'add') {
        const currentState = [...state];
        const { entityId, entityType, cloverPoints } = action.payload;
        const currentStateCloverPoints = currentState.reduce((acc, value) => {
          if (value.cloverPoints && value.cloverPoints.length > 0) {
            value.cloverPoints?.forEach((cloverPoint) => {
              acc.push(cloverPoint.id);
            });
          }
          return acc;
        }, [] as Array<string>);
        const filteringPossibleDuplicateCloverPointsOnPayload = cloverPoints?.filter((cloverPoint) => {
          return !currentStateCloverPoints.includes(cloverPoint.id);
        });
        currentState.push({
          entityId,
          entityType,
          cloverPoints: filteringPossibleDuplicateCloverPointsOnPayload,
        });
        currentState.sort((a, b) => (a.entityType === 'commerce' && b.entityType === 'commerce' ? 1 : -1));
        return currentState;
      } else {
        return initialStateForCloverPoints;
      }
    case 'delete-duplicate-clover-points-in-list':
      if (action.operation === 'delete') {
        const filteringNotPresentCommerceIdsOnState = state.filter((entityElement, index, arr) => {
          return arr.findIndex((item) => item.entityId === entityElement.entityId) === index;
        });
        return filteringNotPresentCommerceIdsOnState;
      } else {
        return initialStateForCloverPoints;
      }
    case 'cleanup-clover-points':
      return initialStateForCloverPoints;
    default:
      return [...state];
  }
};

export const parsePaymentMethod = (t: TFunction, order: Order): string => {
  if (
    order.fiservQrDataPayment ||
    (order.cloverDataPayment && ['Mercado Pago', 'Pix'].includes(order.cloverDataPayment?.paymentMethod))
  ) {
    return t('consumptions-report-paymentMethodTypes.qr', {
      paymentMethod:
        order.cloverDataPayment && ['Mercado Pago', 'Pix'].includes(order.cloverDataPayment?.paymentMethod)
          ? order.cloverDataPayment?.paymentMethod
          : 'Fiserv',
    });
  } else if (order.isCourtesy) {
    return t('consumptions-report-paymentMethodTypes.courtesy');
  } else if (order.isCash) {
    return t('consumptions-report-paymentMethodTypes.cash');
  } else {
    return t('consumptions-report-paymentMethodTypes.card', {
      paymentMethod: order.cloverDataPayment?.paymentMethod,
    });
  }
};

export const parsePaymentMethodByPayment = (t: TFunction, payment: Payment): string => {
  if (payment.isCash) {
    return t('consumptions-report-paymentMethodTypes.cash');
  } else if (payment.isCourtesy) {
    return t('consumptions-report-paymentMethodTypes.courtesy');
  } else if (
    payment.fiservQrPayment ||
    (payment.cloverPointPayment && ['Mercado Pago', 'Pix'].includes(payment.cloverPointPayment?.paymentMethod))
  ) {
    return t('consumptions-report-paymentMethodTypes.qr', {
      paymentMethod:
        payment.cloverPointPayment && ['Mercado Pago', 'Pix'].includes(payment.cloverPointPayment?.paymentMethod)
          ? payment.cloverPointPayment?.paymentMethod
          : 'Fiserv',
    });
  } else {
    return t('consumptions-report-paymentMethodTypes.card', {
      paymentMethod: payment.cloverPointPayment?.paymentMethod,
    });
  }
};

const parseAmount = (amount: number | null, language: SupportedLanguages) => parseAmounts(amount ?? 0, language);

export const parseSplitPaymentText = (subPayments: Payment[], t: TFunction, language: SupportedLanguages) => {
  let paymentText = `${t('consumptions-report-paymentMethodTypes.split-payment')}\n`;
  subPayments.forEach(item => {
    paymentText += `· ${parsePaymentMethodByPayment(t, item)} - ${parseAmount(item.partialAmount, language)}, `;
  });
  return paymentText;

}

